import React from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Space, Tag } from 'antd';
import type { DeviceSourceDto } from '@wellverse/shared-ts';
import { BodyMetricSourceType } from '@wellverse/shared-ts';

const sourceColors: Record<BodyMetricSourceType, string> = {
  [BodyMetricSourceType.MANUAL]: '#898989',
  [BodyMetricSourceType.APPLE_HEALTH]: '#52c41a',
  [BodyMetricSourceType.GOOGLE_FIT]: '#1890ff',
  [BodyMetricSourceType.GARMIN]: '#007cc3',
  [BodyMetricSourceType.FITBIT]: '#00b0b9',
  [BodyMetricSourceType.SAMSUNG_HEALTH]: '#1428a0',
  [BodyMetricSourceType.WITHINGS]: '#4bc9c0',
  [BodyMetricSourceType.XIAOMI]: '#ff6900',
  [BodyMetricSourceType.RENPHO]: '#7b2cbf',
  [BodyMetricSourceType.OPPO]: '#6b2737',
  [BodyMetricSourceType.HUAWEI_HEALTH]: '#cf222e',
  [BodyMetricSourceType.APPLE_WATCH]: '#000000',
  [BodyMetricSourceType.GARMIN_WATCH]: '#007cc3',
  [BodyMetricSourceType.POLAR]: '#6b4c9a',
  [BodyMetricSourceType.OTHER]: '#595959',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const truncateUserId = (id: string) => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

export const BodyMetricSourceList: React.FC = () => {
  const { tableProps } = useTable<DeviceSourceDto>({
    resource: 'body-metric-admin/sources',
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="body-metric-admin/sources">
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }}>
        <Table.Column
          dataIndex="userId"
          title="User ID"
          render={(id: string) => truncateUserId(id)}
        />
        <Table.Column
          dataIndex="sourceType"
          title="Source Type"
          filters={Object.values(BodyMetricSourceType).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: DeviceSourceDto) => record.sourceType === value}
          render={(source: BodyMetricSourceType) => (
            <Tag style={tagStyle(sourceColors[source] || '#898989')}>{source}</Tag>
          )}
        />
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="deviceId" title="Device ID" />
        <Table.Column
          dataIndex="lastSyncedAt"
          title="Last Synced At"
          render={(date: string | undefined) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="isActive"
          title="Active"
          filters={[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]}
          onFilter={(value, record: DeviceSourceDto) => record.isActive === value}
          render={(isActive: boolean) => (
            <Tag style={tagStyle(isActive ? '#3ecf8e' : '#898989')}>
              {isActive ? 'Yes' : 'No'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: DeviceSourceDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="body-metric-admin/sources" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};